import {
  ComponentType,
  type Component,
  type ComponentUpdateWithBreakpoint,
} from "../../types";
import { deepMergeBreakpoints } from "../../utils/deepMergeBreakpoints";
import { enhanceComponentsWithChildren } from "./editor-utils";
import {
  collectComponentTreeForClone,
  collectComponentTreesForClone,
  createComponentClonePlan,
  flattenComponentsForClone,
  getTopLevelCloneRootIds,
  isResponsiveBreakpointKey,
  toIdMap,
  typedObjectKeys,
} from "./editor-store-helpers";
import {
  markCollaborationUpdateIfNeeded,
  notifyResponsiveComponentRemoved,
  syncCurrentPageState,
} from "./component-slice-helpers";
import type {
  ComponentSlice,
  ComponentSliceState,
} from "./component-slice-types";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";
import type { CollaborationBridgeStore } from "./collaboration-bridge-types";
import { VersionIntegrationContext } from "./versioning-integration";

export function createComponentMutationActions<
  TState extends ComponentSliceState,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
  getCollaborationBridge: () => CollaborationBridgeStore | null,
): Pick<
  ComponentSlice,
  | "updateComponent"
  | "deleteComponent"
  | "duplicateComponent"
  | "duplicateComponents"
  | "moveComponent"
  | "resizeComponent"
  | "reorderComponent"
> {
  return {
    updateComponent: (id, updates, options) => {
      const oldComponent = get().getComponent(id);

      try {
        const bridgeStore = getCollaborationBridge();
        if (bridgeStore) {
          const bridge = bridgeStore.getState();
          if (bridge.isCollaborationActive) {
            markCollaborationUpdateIfNeeded();
            bridge.interceptComponentUpdate(id, updates);
            return;
          }
        }
      } catch {
        // Collaboration bridge not available, continue with local update.
      }

      const currentState = get();
      if (
        currentState.versioningEnabled &&
        oldComponent &&
        options?.skipVersionTracking !== true
      ) {
        try {
          const significantFields: (keyof Component)[] = [
            "name",
            "type",
            "properties",
            "style",
            "position",
            "size",
          ];
          const { breakpoint: _breakpoint, ...componentUpdates } = updates;
          const changedFields = typedObjectKeys(componentUpdates).filter(
            (key) =>
              significantFields.includes(key) &&
              componentUpdates[key] !== oldComponent[key],
          );

          if (changedFields.length > 0) {
            const versionContext = VersionIntegrationContext.getInstance();
            changedFields.forEach((field) => {
              const fieldName = String(field);
              versionContext.trackComponentChange(
                "update",
                id,
                oldComponent.pageId,
                oldComponent[field],
                componentUpdates[field],
                {
                  field: fieldName,
                  description: `Updated ${fieldName} of ${oldComponent.name || oldComponent.type} component`,
                },
              );
            });
          }
        } catch (error) {
          console.warn("Version tracking failed for component update:", error);
        }
      }

      let finalUpdates: ComponentUpdateWithBreakpoint = { ...updates };
      const updateBreakpoint = updates.breakpoint;

      if (updateBreakpoint && updates.size && oldComponent) {
        const existingResponsivePosition = oldComponent.responsivePosition || {};
        const existingBreakpoints = existingResponsivePosition.breakpoints || {};
        const existingBreakpointData = isResponsiveBreakpointKey(
          updateBreakpoint,
        )
          ? existingBreakpoints[updateBreakpoint] || {}
          : {};
        const existingBreakpointSize = existingBreakpointData.size || {};

        finalUpdates.responsivePosition = {
          ...existingResponsivePosition,
          breakpoints: {
            ...existingBreakpoints,
            [updateBreakpoint]: {
              ...existingBreakpointData,
              size: {
                ...existingBreakpointSize,
                ...updates.size,
              },
            },
          },
        };

        delete finalUpdates.size;
        delete finalUpdates.breakpoint;
      } else if (finalUpdates.breakpoint) {
        delete finalUpdates.breakpoint;
      }

      set((state) => {
        const index = state.components.findIndex((component) => component.id === id);
        if (index === -1) {
          return;
        }

        const existingComponent = state.components[index];
        const mergedProperties = finalUpdates.properties
          ? { ...existingComponent.properties, ...finalUpdates.properties }
          : existingComponent.properties;
        const mergedStyle = finalUpdates.style
          ? { ...existingComponent.style, ...finalUpdates.style }
          : existingComponent.style;
        const shouldMergeBreakpoints = options?.mergeBreakpoints ?? true;
        const mergedResponsivePosition = finalUpdates.responsivePosition
          ? shouldMergeBreakpoints
            ? {
                ...existingComponent.responsivePosition,
                ...finalUpdates.responsivePosition,
                breakpoints: deepMergeBreakpoints(
                  existingComponent.responsivePosition?.breakpoints,
                  finalUpdates.responsivePosition?.breakpoints,
                ),
              }
            : finalUpdates.responsivePosition
          : existingComponent.responsivePosition;

        const nextComponents = [...state.components];
        nextComponents[index] = {
          ...existingComponent,
          ...finalUpdates,
          properties: mergedProperties,
          style: mergedStyle,
          responsivePosition: mergedResponsivePosition,
          updatedAt: new Date(),
        };

        const shouldEnhance =
          nextComponents[index].type === ComponentType.FLEX_CONTAINER &&
          updates.children !== undefined;

        const normalizedComponents =
          shouldEnhance || nextComponents[index].parentId
            ? enhanceComponentsWithChildren(nextComponents)
            : nextComponents;

        state.components = normalizedComponents;
        state.componentMap = toIdMap(normalizedComponents);
        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        syncCurrentPageState(state);
      });

      get().pushToHistory();
    },

    deleteComponent: (id) => {
      const componentToDelete = get().getComponent(id);

      try {
        const bridgeStore = getCollaborationBridge();
        if (bridgeStore) {
          const bridge = bridgeStore.getState();
          if (bridge.isCollaborationActive) {
            markCollaborationUpdateIfNeeded();
            bridge.interceptComponentDelete(id);
            return;
          }
        }
      } catch {
        // Collaboration bridge not available, continue with local update.
      }

      if (get().versioningEnabled && componentToDelete) {
        VersionIntegrationContext.getInstance().trackComponentChange(
          "delete",
          id,
          componentToDelete.pageId,
          componentToDelete,
          undefined,
          {
            description: `Deleted ${componentToDelete.name || componentToDelete.type} component`,
          },
        );
      }

      set((state) => {
        const idsToRemove = new Set<string>();

        const collectIds = (componentId: string) => {
          const component = state.componentMap.get(componentId);
          if (!component || idsToRemove.has(componentId)) {
            return;
          }

          idsToRemove.add(componentId);
          component.children.forEach(collectIds);
        };

        collectIds(id);

        const remainingComponents = state.components
          .filter((component) => !idsToRemove.has(component.id))
          .map((component) =>
            idsToRemove.has(component.parentId ?? "")
              ? { ...component, parentId: undefined }
              : {
                  ...component,
                  children: component.children.filter(
                    (childId) => !idsToRemove.has(childId),
                  ),
                },
          );

        const enhancedComponents =
          enhanceComponentsWithChildren(remainingComponents);

        state.components = enhancedComponents;
        state.componentMap = toIdMap(enhancedComponents);
        state.selectedComponents = state.selectedComponents.filter(
          (selectedId) => !idsToRemove.has(selectedId),
        );
        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        syncCurrentPageState(state);
      });

      get().pushToHistory();
      notifyResponsiveComponentRemoved(id);
    },

    duplicateComponent: (id) => {
      const componentsToDuplicate = collectComponentTreeForClone(
        id,
        get().components,
      );
      const component = componentsToDuplicate.find(
        (candidate) => candidate.id === id,
      );

      if (!component || componentsToDuplicate.length === 0) {
        return;
      }

      const duplicatePlan = createComponentClonePlan(componentsToDuplicate, {
        positionOffset: {
          x: 20,
          y: 20,
        },
        preserveExternalParentIds: true,
        rootIds: new Set([id]),
        rootNameSuffix: "Copy",
      });
      const duplicatedRootId = duplicatePlan.idMap.get(id);

      if (!duplicatedRootId) {
        return;
      }

      set((state) => {
        let nextComponents = [
          ...flattenComponentsForClone(state.components),
          ...duplicatePlan.components,
        ];

        if (component.parentId) {
          nextComponents = nextComponents.map((candidate) =>
            candidate.id !== component.parentId
              ? candidate
              : {
                  ...candidate,
                  children: candidate.children.includes(duplicatedRootId)
                    ? candidate.children
                    : [...candidate.children, duplicatedRootId],
                },
          );
        }

        const enhancedComponents = enhanceComponentsWithChildren(nextComponents);

        state.components = enhancedComponents;
        state.componentMap = toIdMap(enhancedComponents);
        state.selectedComponents = [duplicatedRootId];
        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        syncCurrentPageState(state);
      });

      get().pushToHistory();
    },

    duplicateComponents: (ids) => {
      const rootIds = getTopLevelCloneRootIds(ids, get().components);
      const componentsToDuplicate = collectComponentTreesForClone(
        rootIds,
        get().components,
      );

      if (rootIds.length === 0 || componentsToDuplicate.length === 0) {
        return;
      }

      const duplicatePlan = createComponentClonePlan(componentsToDuplicate, {
        positionOffset: {
          x: 20,
          y: 20,
        },
        preserveExternalParentIds: true,
        rootIds: new Set(rootIds),
        rootNameSuffix: "Copy",
      });
      const duplicatedRootIds = rootIds
        .map((rootId) => duplicatePlan.idMap.get(rootId))
        .filter((duplicatedId): duplicatedId is string => duplicatedId !== undefined);

      if (duplicatedRootIds.length === 0) {
        return;
      }

      set((state) => {
        let nextComponents = [
          ...flattenComponentsForClone(state.components),
          ...duplicatePlan.components,
        ];

        rootIds.forEach((rootId) => {
          const sourceRoot = componentsToDuplicate.find(
            (component) => component.id === rootId,
          );
          const duplicatedRootId = duplicatePlan.idMap.get(rootId);
          if (!sourceRoot?.parentId || !duplicatedRootId) {
            return;
          }

          nextComponents = nextComponents.map((candidate) =>
            candidate.id !== sourceRoot.parentId
              ? candidate
              : {
                  ...candidate,
                  children: candidate.children.includes(duplicatedRootId)
                    ? candidate.children
                    : [...candidate.children, duplicatedRootId],
                },
          );
        });

        const enhancedComponents = enhanceComponentsWithChildren(nextComponents);

        state.components = enhancedComponents;
        state.componentMap = toIdMap(enhancedComponents);
        state.selectedComponents = duplicatedRootIds;
        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        syncCurrentPageState(state);
      });

      get().pushToHistory();
    },

    moveComponent: (id, position) => {
      const oldComponent = get().getComponent(id);

      try {
        const bridgeStore = getCollaborationBridge();
        if (bridgeStore) {
          const bridge = bridgeStore.getState();
          if (bridge.isCollaborationActive) {
            markCollaborationUpdateIfNeeded();
            bridge.interceptComponentMove(id, position);
            return;
          }
        }
      } catch {
        // Collaboration bridge not available, continue with local update.
      }

      if (get().versioningEnabled && oldComponent) {
        VersionIntegrationContext.getInstance().trackComponentChange(
          "move",
          id,
          oldComponent.pageId,
          oldComponent.position,
          position,
          {
            field: "position",
            description: `Moved ${oldComponent.name || oldComponent.type} component`,
          },
        );
      }

      get().updateComponent(id, { position }, { skipVersionTracking: true });
    },

    resizeComponent: (id, size) => {
      const oldComponent = get().getComponent(id);

      try {
        const bridgeStore = getCollaborationBridge();
        if (bridgeStore) {
          const bridge = bridgeStore.getState();
          if (bridge.isCollaborationActive) {
            markCollaborationUpdateIfNeeded();
            bridge.interceptComponentResize(id, size);
            return;
          }
        }
      } catch {
        // Collaboration bridge not available, continue with local update.
      }

      if (get().versioningEnabled && oldComponent) {
        VersionIntegrationContext.getInstance().trackComponentChange(
          "resize",
          id,
          oldComponent.pageId,
          oldComponent.size,
          size,
          {
            field: "size",
            description: `Resized ${oldComponent.name || oldComponent.type} component`,
          },
        );
      }

      get().updateComponent(id, { size }, { skipVersionTracking: true });
    },

    reorderComponent: (id, newOrder) => {
      get().updateComponent(id, { order: newOrder });
    },
  };
}
